import { lazy } from 'react';

// project imports
import Loadable from 'components/Loadable';
import DashboardLayout from 'layout/Dashboard';
import AuthGuard from 'components/AuthGuard';
import AdminGuard from 'components/AdminGuard';
import InstructorGuard from 'components/InstructorGuard';
import RoleHome from 'components/RoleHome';

// pages
const Home = Loadable(lazy(() => import('pages/Home')));
const Account = Loadable(lazy(() => import('pages/Account')));
const CourseDetail = Loadable(lazy(() => import('pages/courses/CourseDetail')));
const LessonPlayer = Loadable(lazy(() => import('pages/lessons/LessonPlayer')));
const AdminDashboard = Loadable(lazy(() => import('pages/admin/AdminDashboard')));
const AdminCourses = Loadable(lazy(() => import('pages/admin/AdminCourses')));
const AdminCourseRoster = Loadable(lazy(() => import('pages/admin/AdminCourseRoster')));
const AdminCourseEditor = Loadable(lazy(() => import('pages/admin/AdminCourseEditor')));
const AdminStudents = Loadable(lazy(() => import('pages/admin/AdminStudents')));
const AdminStudentDetail = Loadable(lazy(() => import('pages/admin/AdminStudentDetail')));
const AdminIntegrations = Loadable(lazy(() => import('pages/admin/AdminIntegrations')));
const AdminOrganizations = Loadable(lazy(() => import('pages/admin/AdminOrganizations')));
const AdminAdministrators = Loadable(lazy(() => import('pages/admin/AdminAdministrators')));
const AdminInstructors = Loadable(lazy(() => import('pages/admin/AdminInstructors')));
const AdminAccess = Loadable(lazy(() => import('pages/admin/AdminAccess')));
const InstructorCourses = Loadable(lazy(() => import('pages/instructor/InstructorCourses')));
const InstructorCourseRoster = Loadable(lazy(() => import('pages/instructor/InstructorCourseRoster')));

// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes = {
  // No `path` here — this is a pathless layout wrapper now that nothing in
  // it is registered at '/'. With a path of '/' this route object matched
  // the bare '/' URL by itself (independent of whether any child matched),
  // which out-ranked PublicRoutes for that exact URL by array order and
  // sent every logged-out visitor straight to /login instead of the
  // marketing page — found via browser verification, not by inspection.
  element: (
    <AuthGuard>
      <DashboardLayout />
    </AuthGuard>
  ),
  children: [
    {
      path: 'dashboard',
      element: (
        <RoleHome>
          <Home />
        </RoleHome>
      )
    },
    {
      path: 'account',
      element: <Account />
    },
    {
      path: 'courses/:slug',
      element: <CourseDetail />
    },
    {
      path: 'courses/:slug/lessons/:lessonId',
      element: <LessonPlayer />
    },
    {
      path: 'admin',
      element: (
        <AdminGuard>
          <AdminDashboard />
        </AdminGuard>
      )
    },
    {
      path: 'admin/courses',
      element: (
        <AdminGuard>
          <AdminCourses />
        </AdminGuard>
      )
    },
    {
      path: 'admin/courses/:slug',
      element: (
        <AdminGuard>
          <AdminCourseRoster />
        </AdminGuard>
      )
    },
    {
      path: 'admin/courses/:slug/edit',
      element: (
        <AdminGuard>
          <AdminCourseEditor />
        </AdminGuard>
      )
    },
    {
      path: 'admin/students',
      element: (
        <AdminGuard>
          <AdminStudents />
        </AdminGuard>
      )
    },
    {
      path: 'admin/students/:id',
      element: (
        <AdminGuard>
          <AdminStudentDetail />
        </AdminGuard>
      )
    },
    {
      path: 'admin/organizations',
      element: (
        <AdminGuard>
          <AdminOrganizations />
        </AdminGuard>
      )
    },
    {
      path: 'admin/settings/access',
      element: (
        <AdminGuard>
          <AdminAccess />
        </AdminGuard>
      )
    },
    {
      path: 'admin/settings/administrators',
      element: (
        <AdminGuard>
          <AdminAdministrators />
        </AdminGuard>
      )
    },
    {
      path: 'admin/settings/instructors',
      element: (
        <AdminGuard>
          <AdminInstructors />
        </AdminGuard>
      )
    },
    {
      path: 'admin/settings/integrations',
      element: (
        <AdminGuard>
          <AdminIntegrations />
        </AdminGuard>
      )
    },
    {
      path: 'instructor',
      element: (
        <InstructorGuard>
          <InstructorCourses />
        </InstructorGuard>
      )
    },
    {
      path: 'instructor/courses/:slug',
      element: (
        <InstructorGuard>
          <InstructorCourseRoster />
        </InstructorGuard>
      )
    }
  ]
};

export default MainRoutes;
